import ExcelJS from 'exceljs';
import { CargoEventReportData } from '../../ReportGeneratorTypes';
import { setCellValueWithFont, setupWorksheetForPrint, setWorksheetPrintArea } from './reportEventExcelUtils';
import { addOdfjellHeader, addVesselDetails, odfjellHeaderColor } from './parts/helpers';
import { EventTypeEnum } from 'src/enums/eventTypeEnum';
import { TankersEvent } from 'src/types/ViewModel/TankersEvent';
import { toDateTime } from 'odfjell-date-time-utils/dateTimeConverter';

const addReportHeader = (worksheet: ExcelJS.Worksheet, logoImageId: number) => {
    let rowNum = addOdfjellHeader(worksheet, logoImageId);
    rowNum = 6;
    setCellValueWithFont(worksheet, rowNum, 1, `LETTER OF PROTEST`, {
        bold: true,
        color: odfjellHeaderColor,
        fontSize: 16,
        mergeAcross: 9,
    });

    rowNum++;

    setCellValueWithFont(worksheet, rowNum, 1, `FREE PRATIQUE GRANTED MORE THAN 6 HOURS AFTER TENDERING NOR`, {
        bold: true,
        color: odfjellHeaderColor,
        fontSize: 12,
        mergeAcross: 9,
    });

    return rowNum + 1;
};

const formatEventDate = (event: TankersEvent) => {
    return toDateTime(event.eventDate.actualDateTimeLocal).toFormat('MM/dd/yyyy');
};
const formatEventTime = (event: TankersEvent) => {
    return toDateTime(event.eventDate.actualDateTimeLocal).toFormat('HH:mm:ss');
};

const addArrived = (worksheet: ExcelJS.Worksheet, arrivalEvent: TankersEvent, rowNum: number) => {
    setCellValueWithFont(worksheet, rowNum, 7, 'Arrived:', {
        align: 'right',
    });
    setCellValueWithFont(worksheet, rowNum, 8, formatEventDate(arrivalEvent), {
        align: 'left',
    });
    setCellValueWithFont(worksheet, rowNum, 9, formatEventTime(arrivalEvent), {
        align: 'left',
    });
    return rowNum + 1;
};

const addContent = (worksheet: ExcelJS.Worksheet, norTenderEvent: TankersEvent, evParent: TankersEvent, rowNum: number) => {
    // Paragraphs
    rowNum += 2;
    const pStyle = { mergeAcross: 9, mergeDown: 2, verticalAlign: 'top', wrapText: true, align: 'left' } as const;
    const paragraphs = [
        'On behalf of my Owners, I hereby note protest since Free Pratique was granted more than 6 hours after Tendering notice of readiness. ',
        'I therefore reserve the right of my Owners to refer to this matter at some later date and take any action, or make any claims, as they deem necessary. ',
    ];
    paragraphs.forEach((text) => {
        setCellValueWithFont(worksheet, rowNum, 1, text, pStyle as any);
        rowNum += 3;
    });

    // Helper to write a label + optional date/time on the same row
    const writeDateRow = (label: string, ev?: TankersEvent) => {
        setCellValueWithFont(worksheet, rowNum, 1, label, { mergeAcross: 5, wrapText: true, align: 'left' });
        if (ev?.eventDate?.actualDateTimeLocal) {
            setCellValueWithFont(worksheet, rowNum, 6, formatEventDate(ev), { wrapText: true, align: 'left' });
            setCellValueWithFont(worksheet, rowNum, 7, formatEventTime(ev), { wrapText: true, align: 'left' });
        }
    };

    // Time rows
    writeDateRow('Time of Free Pratique:', evParent);
    rowNum += 2;
    writeDateRow('Time of Tendering Notice of readiness:', norTenderEvent);

    return rowNum;
};

const addSignature = (worksheet: ExcelJS.Worksheet, rowNumber: number): number => {
    rowNumber += 7;

    const drawLineRow = () => {
        const lineStyle = { mergeAcross: 2, border: { bottom: { style: 'thin' as const } }, align: 'left' };
        setCellValueWithFont(worksheet, rowNumber, 3, '', lineStyle as any);
        setCellValueWithFont(worksheet, rowNumber, 7, '', lineStyle as any);
    };
    const writeLabels = (left: string, right: string) => {
        rowNumber++;
        setCellValueWithFont(worksheet, rowNumber, 3, left, { mergeAcross: 2, align: 'left' });
        setCellValueWithFont(worksheet, rowNumber, 7, right, { mergeAcross: 2, align: 'left' });
    };

    const blocks = [
        { left: 'Master', right: 'Date', gapAfter: 4 },
        { left: 'Sign', right: 'Date', gapAfter: 0 },
    ] as const;

    blocks.forEach((b, idx) => {
        drawLineRow();
        writeLabels(b.left, b.right);
        if (b.gapAfter) rowNumber += b.gapAfter;
    });

    return rowNumber + 6;
};

const setDefaultColumnWidths = (workheet: ExcelJS.Worksheet) => {
    workheet.getColumn(1).width = 9;
    workheet.getColumn(2).width = 2;
    workheet.getColumn(3).width = 14;
    workheet.getColumn(4).width = 21;
    workheet.getColumn(5).width = 9;
    workheet.getColumn(6).width = 17;
    workheet.getColumn(7).width = 10;
    workheet.getColumn(8).width = 14;
    workheet.getColumn(9).width = 9;
};

export const generateProtestFreePratiqueReport = (
    workbook: ExcelJS.Workbook,
    cargoEventReportData: CargoEventReportData,
    logoImageId: number
): void => {
    const _worksheet = workbook.addWorksheet('Protest Free Pratique');
    setupWorksheetForPrint(_worksheet);
    // Hide gridlines and keep a clean white background
    _worksheet.views = [{ state: 'normal', showGridLines: false }];
    const portCall = cargoEventReportData.selectedPortCall;
    const selectedEvent = cargoEventReportData.tankersEvents.find((ev) => !ev.deleted && ev.eventId === cargoEventReportData.excelReportInfo.eventId);
    const events = cargoEventReportData.tankersEvents.filter(
        (ev) => !ev.deleted && ev.eventTypeContextId === 2 && ev.portCallId === portCall.imosPortCallId
    );
    const arrivalEvent = events.find((e) => e.eventTypeId === EventTypeEnum.Arrived);
    const norTenderEvent = events.find((e) => e.eventTypeId === EventTypeEnum.NorTender);
    const evParent = events.find(
        (o) =>
            o.eventTypeId === EventTypeEnum.FreePratique &&
            o.eventTypeGroupSequenceIdentity === selectedEvent.eventTypeGroupSequenceDependencyIdentity
    );

    setDefaultColumnWidths(_worksheet);
    addReportHeader(_worksheet, logoImageId);
    let rowNum = addVesselDetails(_worksheet, 9, 1, 7, portCall.voyageNumber, portCall.portDisplayName, cargoEventReportData.vessel, null, null);
    rowNum = addArrived(_worksheet, arrivalEvent, rowNum);
    rowNum = addContent(_worksheet, norTenderEvent, evParent, rowNum);
    rowNum = addSignature(_worksheet, rowNum);
    setWorksheetPrintArea(_worksheet, rowNum, 9, 1, 1);
};
